import { Badge } from '@vrplatform/components';
import {
  CalendarEventIcon,
  CircleDollarIcon,
  ClipboardTextIcon,
  FileCheckIcon,
  FolderOpenIcon,
  HashtagIcon,
  Icon,
  LoaderIcon,
  Lock2Icon,
  NoteTextIcon,
  ShareIcon,
} from '@vrplatform/icons';
import {
  day,
  ensure,
  formatCurrency,
  hasValue,
  type Maybe,
} from '@vrplatform/utils';
import {
  Center,
  Group,
  Skeleton,
  Stack,
  Text,
  useMantineTheme,
} from '@mantine/core';
import { type ComponentProps, useMemo } from 'react';
import type { DepositRow } from '../../views/deposits/list/useDepositsTableQuery';
import {
  DrawerInfoCard,
  DrawerInfoCardLockBadges,
  DrawerInfoCardOwnerStatementsLinks,
} from '../_components';
import { getDrawerInfoCardLockReasonBadges } from '../_components/DrawerInfoCardLockBadges.utils';
import { useBankRecordDetailDrawer } from '../bank-record-drawer';
import { DepositInfoCardBankAccountSelect } from './DepositInfoCardBankAccountSelect';
import { AppIconById } from '../../components';
import { useApiQuery } from '@vrplatform/data';

type Props = {
  deposit: DepositRow;
  type: 'drawer' | 'page';
};

export const DepositInfoCard = ({ deposit, type }: Props) => {
  const bankRecordId = deposit.payment.bankRecordIds?.[0];
  const lockReasonBadges = useMemo(
    () =>
      getDrawerInfoCardLockReasonBadges({
        sources: [
          {
            isLocked: deposit.lock?.status === 'locked',
            lock: deposit.lock,
          },
        ],
        fallbackBadge: {
          label: 'Locked',
          tooltip: deposit.lock?.message,
        },
      }),
    [deposit.lock, deposit.lock?.message]
  );

  const rows = useMemo(
    () =>
      ensure<
        (ComponentProps<typeof DrawerInfoCard>['rows'][number] | undefined)[]
      >([
        {
          icon: LoaderIcon,
          title: 'Status',
          text:
            deposit.status !== 'inactive' ? null : (
              <Badge color="orange" size="md">
                Archived
              </Badge>
            ),
        },
        {
          icon: CircleDollarIcon,
          title: 'Opening Balance',
          text: !deposit.isOpeningBalance ? null : (
            <Badge color="blue" size="md">
              Opening Balance
            </Badge>
          ),
        },
        type === 'drawer'
          ? undefined
          : {
              icon: ShareIcon,
              title: 'Synced from',
              text: deposit.connection?.id ? (
                <Group gap="xs" wrap="nowrap">
                  {deposit.connection.appId && (
                    <AppIconById size={20} appId={deposit.connection.appId} />
                  )}
                  <Text>{deposit.connection.name}</Text>
                </Group>
              ) : null,
            },
        lockReasonBadges.length
          ? {
              icon: Lock2Icon,
              title: 'Locked due to',
              text: <DrawerInfoCardLockBadges badges={lockReasonBadges} />,
            }
          : undefined,
        type === 'page'
          ? undefined
          : {
              icon: CircleDollarIcon,
              title: 'Amount',
              text: formatCurrency(
                deposit.totals.total / 100,
                deposit.currency
              ),
            },
        {
          icon: CalendarEventIcon,
          title: 'Date',
          text: day(deposit.date).format('MMM D, YYYY'),
        },
        {
          icon: FolderOpenIcon,
          title: 'Bank/CC account',
          text:
            deposit.connection?.id && !deposit.isReconciled ? (
              <DepositInfoCardBankAccountSelect
                bankAccount={deposit.bankAccount}
                depositId={deposit.id}
              />
            ) : (
              deposit.bankAccount?.title
            ),
        },
        {
          icon: HashtagIcon,
          title: 'Reference',
          text: deposit.uniqueRef,
        },
        {
          icon: NoteTextIcon,
          title: 'Description',
          text: deposit.description,
        },
        {
          icon: FileCheckIcon,
          title: 'Reconciliation',
          text: <InfoCardReconciliationItem bankRecordIds={[bankRecordId]} />,
        },
        {
          icon: ClipboardTextIcon,
          title: 'Owner Statements',
          text: deposit.attachedToOwnerStatementIds.length && (
            <DrawerInfoCardOwnerStatementsLinks
              ids={deposit.attachedToOwnerStatementIds}
            />
          ),
        },
      ]).filter(hasValue),
    [
      deposit.bankAccount,
      lockReasonBadges,
      deposit.status,
      deposit.isOpeningBalance,
      deposit.bankAccount?.title,
      deposit.id,
      deposit.totals.total,
      deposit.connection?.name,
      deposit.connection?.appId,
      deposit.connection?.id,
      deposit.currency,
      deposit.date,
      deposit.description,
      deposit.uniqueRef,
      deposit.isReconciled,
      deposit.attachedToOwnerStatementIds,
      bankRecordId,
      type,
    ]
  );

  return <DrawerInfoCard labelWidth={150} rows={rows} />;
};

function useBankRecordsQuery(bankRecordIds: Maybe<string>[] | undefined) {
  const ids = bankRecordIds?.filter(hasValue);

  return useApiQuery(
    async (api, signal, args) => {
      if (!args.bankRecordIds?.length)
        return {
          bankRecords: null,
        };
      const bankRecords = await Promise.all(
        args.bankRecordIds.map((id) =>
          api
            .GET('/bank-records/{id}', {
              params: {
                path: {
                  id,
                },
              },
              signal,
            })
            .then((result) => {
              if (result.error) throw result.error;
              return result.data;
            })
        )
      );

      return {
        bankRecords,
      };
    },
    {
      skip: !ids?.length,
      queryGroup: 'deposits',
      uniqueQueryKey: `bank-record-${ids?.join(',')}`,
      variables: {
        bankRecordIds: ids,
      },
      keepPreviousData: true,
    }
  );
}

export const InfoCardReconciliationItem = ({
  bankRecordIds,
}: {
  bankRecordIds: string[] | undefined;
}) => {
  const queryData = useBankRecordsQuery(bankRecordIds);

  if (queryData.isLoading) return <Skeleton height={20} width={80} />;

  const bankRecords = queryData.data?.bankRecords;

  if ((bankRecords?.length || 0) > 1) {
    return (
      <Stack gap={6}>
        {bankRecords?.map((bankRecord) => (
          <ReconciliationItem
            key={bankRecord.id}
            bankRecord={bankRecord}
            iconSize={6}
          />
        ))}
      </Stack>
    );
  }

  return <ReconciliationItem bankRecord={bankRecords?.[0]} />;
};

const ReconciliationItem = ({
  bankRecord,
  iconSize = 8,
}: {
  bankRecord:
    | NonNullable<
        NonNullable<
          ReturnType<typeof useBankRecordsQuery>['data']
        >['bankRecords']
      >[number]
    | undefined;
  iconSize?: number;
}) => {
  const { open: openBankRecord } = useBankRecordDetailDrawer();
  const theme = useMantineTheme();

  return (
    <Group
      gap="xs"
      component={'button'}
      onClick={() => bankRecord?.id && openBankRecord(bankRecord.id, 'push')}
      sx={(theme) => ({
        cursor: bankRecord ? 'pointer' : 'default',
        border: 'none',
        backgroundColor: 'transparent',
        padding: 0,
        flexWrap: 'nowrap',
        color: bankRecord
          ? theme.colors[theme.primaryColor][6]
          : theme.colors.gray[5],
        '&:hover': bankRecord
          ? {
              textDecoration: 'underline',
            }
          : undefined,
      })}
    >
      <Center
        sx={{
          flexShrink: 0,
          width: iconSize * 2,
          height: iconSize * 2,
          backgroundColor: theme.colors[bankRecord ? 'green' : 'yellow'][6],
          borderRadius: theme.radius.xl,
        }}
      >
        <Icon
          icon={bankRecord ? 'CheckIcon' : 'CrossIcon'}
          color={'white'}
          size={iconSize}
          strokeWidth={iconSize / 2}
        />
      </Center>
      <Text span ta="left" lineClamp={2}>
        {bankRecord ? (bankRecord.description ?? '-') : 'Not matched yet'}
      </Text>
    </Group>
  );
};
